import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import parseISO from 'date-fns/parseISO';
import subMilliseconds from 'date-fns/subMilliseconds';
import {MeetingDto, WorkingHoursDto, MeetingSuggestionDto} from '@lib/infrastructure';
import {numericCompareAsc, parseLocalDateWorkaround, stringCompare} from '@lib/utils';
import {
  AttendeeAvailability,
  AttendeeAvailabilityStatus,
  FeatureToggle,
  Meeting,
  MeetingAttendee,
  MeetingResponseStatus,
  WorkingHours,
  MeetingsUserPreferences,
} from '../entities';
import {GroupedMeetingAttendeesItem} from './types';
import groupBy from 'lodash/groupBy';
import {RootState} from '../../rootReducer';
import {TranslationService} from '../../@types/i18next';

export const filterMeetingsByDate = (meetings: Meeting[], date: Date) => {
  return meetings.filter((meeting) => {
    const parsedStartDate = parseISO(meeting.startDateTime);
    const nonOverlappingEndDate = subMilliseconds(parseISO(meeting.endDateTime), 1);
    return (
      isSameDay(date, parsedStartDate) ||
      isSameDay(date, nonOverlappingEndDate) ||
      (isBefore(parsedStartDate, nonOverlappingEndDate) && // Handles meetings without duration
        isWithinInterval(date, {
          start: parsedStartDate,
          end: nonOverlappingEndDate,
        }))
    );
  });
};

export const findAttendeeByDirectoryObjectId = (attendees: MeetingAttendee[], userDirectoryObjectId: string) =>
  attendees.find((attendee) => attendee.directoryObjectId === userDirectoryObjectId);

export const getGroupedMeetingAttendeesTitle = (t: TranslationService, data: GroupedMeetingAttendeesItem) => {
  if (!data) {
    return '';
  }

  const {locationType, locationDescription} = data;
  switch (locationType) {
    case 'Local':
      return locationDescription ?? '';
    case 'Remote':
      return t('meeting:LocationRemote');
    case 'Unknown':
      return t('meeting:LocationUnknown');
    default:
      return '';
  }
};

export const formatDuration = (
  t: TranslationService,
  duration: Duration | undefined,
  showFullFormat: boolean = false,
) => {
  const {months = 0, days = 0, hours = 0, minutes = 0} = duration ?? {};
  const dynamicLocalizationKeySuffix = showFullFormat ? '_full' : '';
  let durationText = '';

  if (months) {
    // currently only support full format
    durationText = durationText + t(`MonthsTimeUnitSuffix_full`, {count: months});
  }

  if (days) {
    if (months) {
      durationText = durationText + ' ';
    }
    durationText = durationText + t(`DaysTimeUnitSuffix${dynamicLocalizationKeySuffix}`, {count: days});
  }

  if (hours) {
    if (days || months) {
      durationText = durationText + ' ';
    }
    durationText = durationText + t(`HoursTimeUnitSuffix${dynamicLocalizationKeySuffix}`, {count: hours});
  }

  if (minutes || !durationText) {
    if (hours || days || months) {
      durationText = durationText + ' ';
    }
    durationText = durationText + t(`MinutesTimeUnitSuffix${dynamicLocalizationKeySuffix}`, {count: minutes});
  }
  return durationText;
};

export const mapSingleMeeting = (meeting: MeetingDto): MeetingDto => ({
  ...meeting,
  startDateTime: meeting.isAllDay
    ? parseLocalDateWorkaround(meeting.startDateTime).toISOString()
    : meeting.startDateTime,
  endDateTime: meeting.isAllDay ? parseLocalDateWorkaround(meeting.endDateTime).toISOString() : meeting.endDateTime,
  meetingRooms: meeting.meetingRooms
    .filter((room) => room.id !== '00000000-0000-0000-0000-000000000000')
    .map((room) => ({...room, type: 'meetingRoom', suggestionData: room.id})), // Filters out meeting rooms that are not mapped in Mapiq
  attendees: meeting.attendees.map((attendee) => ({
    ...attendee,
    locationInfoPerDate: Object.keys(attendee.locationInfoPerDate).reduce(
      (acc, curr) => ({...acc, [parseLocalDateWorkaround(curr).toISOString()]: attendee.locationInfoPerDate[curr]}),
      {},
    ),
  })),
});

export const mapMeetings = (meetings: MeetingDto[]): MeetingDto[] => meetings.map(mapSingleMeeting);

export const mapMeetingSuggestions = (suggestions: MeetingSuggestionDto[]): MeetingSuggestionDto[] =>
  suggestions.map((suggestion, index) => ({
    ...suggestion,
    id: index.toString(),
    timeSlotStart: parseISO(suggestion.timeSlotStart).toISOString(), // This is temporary solution to issue with backend-provided ISO date format vs one serialized on mobile side (00Z vs 000Z)
    timeSlotEnd: parseISO(suggestion.timeSlotEnd).toISOString(),
  }));

export const cloudSuiteIntegrationErrors = [
  'CloudSuiteError_Unspecified',
  'CloudSuiteError_Generic',
  'CloudSuiteError_InvalidGrant',
  'CloudSuiteError_MailboxNotEnabledForRestAPI',
  'MeetingsService_ConsentNotGiven',
];

export const compareByPriorityTemplateList = (
  sortingOrder: (string | undefined)[],
  otherBuildingsIndex: number,
  a: GroupedMeetingAttendeesItem,
  b: GroupedMeetingAttendeesItem,
) => {
  const indexA = sortingOrder.indexOf(a.buildingId ?? a.locationType);
  const indexB = sortingOrder.indexOf(b.buildingId ?? b.locationType);

  const first = indexA !== -1 ? indexA : otherBuildingsIndex;
  const second = indexB !== -1 ? indexB : otherBuildingsIndex;
  return numericCompareAsc(first, second);
};

export const compareByTemplateWithStringFallback = (
  sortingOrder: (string | undefined)[],
  otherBuildingsIndex: number,
  a: GroupedMeetingAttendeesItem,
  b: GroupedMeetingAttendeesItem,
) => {
  return (
    compareByPriorityTemplateList(sortingOrder, otherBuildingsIndex, a, b) ||
    stringCompare(a.locationDescription, b.locationDescription)
  );
};

export const compareByMeetingResponse = (
  {responseStatus: firstResponseStatus, displayName: firstDisplayName}: MeetingAttendee,
  {responseStatus: secondResponseStatus, displayName: secondDisplayName}: MeetingAttendee,
) => {
  const meetingResponsePriorityList: MeetingResponseStatus[] = ['Accepted', 'Organizer', 'Tentative', 'Declined'];

  // For the needs of sorting Pending and Tentative are considered the same
  const firstResponseStatusProcessed: MeetingResponseStatus =
    firstResponseStatus === 'Pending' ? 'Tentative' : firstResponseStatus;
  const secondResponseStatusProcessed: MeetingResponseStatus =
    secondResponseStatus === 'Pending' ? 'Tentative' : secondResponseStatus;

  const firstPriorityIndex = meetingResponsePriorityList.indexOf(firstResponseStatusProcessed);
  const secondPriorityIndex = meetingResponsePriorityList.indexOf(secondResponseStatusProcessed);

  const difference = numericCompareAsc(firstPriorityIndex, secondPriorityIndex);
  return difference === 0 ? stringCompare(firstDisplayName, secondDisplayName) : difference;
};

export const compareByAttendeeAvailabilityStatus = (a: AttendeeAvailabilityStatus, b: AttendeeAvailabilityStatus) => {
  const availabilityPriorityList = {
    [AttendeeAvailabilityStatus.Available]: 0,
    [AttendeeAvailabilityStatus.Tentative]: 1,
    [AttendeeAvailabilityStatus.Unknown]: 1,
    [AttendeeAvailabilityStatus.Unavailable]: 2,
  };

  return numericCompareAsc(availabilityPriorityList[a], availabilityPriorityList[b]);
};

export const mapWorkingHoursDtoToWorkingHours = (data: WorkingHoursDto | null): WorkingHours | null => {
  if (!data) {
    return null;
  }
  return {
    timeZoneId: data.ianaLocalTimezone,
    days: data.days.map((day) => {
      return {
        dayName: day.day,
        times: day.times,
      };
    }),
  };
};

export const mapWorkingHoursToWorkingHoursDto = (data: WorkingHours | null | undefined): WorkingHoursDto | null => {
  if (!data) {
    return null;
  }
  return {
    ianaLocalTimezone: data.timeZoneId,
    days: data.days.map((day) => {
      return {
        day: day.dayName,
        times: day.times,
      };
    }),
  };
};

const sortAvailabilitiesByDisplayName = (availabilities: AttendeeAvailability[]) =>
  availabilities ? [...availabilities].sort((a, b) => stringCompare(a.displayName, b.displayName)) : [];

export const sortAttendeeAvailabilities = (
  availabilities: AttendeeAvailability[],
  userDirectoryObjectId: string,
  moveOrganizerToBottom?: boolean,
) => {
  if (!availabilities) return [];

  const availabilityGroups = groupBy(availabilities, (item) => item.availability);

  const available = sortAvailabilitiesByDisplayName(availabilityGroups[AttendeeAvailabilityStatus.Available]);
  const unavailable = sortAvailabilitiesByDisplayName(availabilityGroups[AttendeeAvailabilityStatus.Unavailable]);
  const tentative = sortAvailabilitiesByDisplayName(availabilityGroups[AttendeeAvailabilityStatus.Tentative]);
  const unknown = sortAvailabilitiesByDisplayName(availabilityGroups[AttendeeAvailabilityStatus.Unknown]);

  return [
    ...moveOrganizer(available, userDirectoryObjectId, moveOrganizerToBottom),
    ...moveOrganizer(tentative, userDirectoryObjectId, moveOrganizerToBottom),
    ...moveOrganizer(unknown, userDirectoryObjectId, moveOrganizerToBottom),
    ...moveOrganizer(unavailable, userDirectoryObjectId, moveOrganizerToBottom),
  ];
};

export const compareAttendeeAvailabilities = (
  firstAttendee: AttendeeAvailability,
  secondAttendee: AttendeeAvailability,
) => {
  const {displayName: firstDisplayName, availability: firstAvailability} = firstAttendee;
  const {displayName: secondDisplayName, availability: secondAvailability} = secondAttendee;

  const availabilityComparison = compareByAttendeeAvailabilityStatus(firstAvailability, secondAvailability);

  return availabilityComparison === 0 ? stringCompare(firstDisplayName, secondDisplayName) : availabilityComparison;
};

export const moveOrganizer = (
  availabilities: AttendeeAvailability[],
  organizerDirectoryObjectId: string,
  moveToBottom?: boolean,
) => {
  const organizer = availabilities.find((item) => item.directoryObjectId === organizerDirectoryObjectId);
  const rest = availabilities.filter((item) => item.directoryObjectId !== organizerDirectoryObjectId);

  if (!organizer || availabilities.length <= 1) {
    return availabilities;
  }

  return moveToBottom ? [...rest, organizer] : [organizer, ...rest];
};

export const isHybridMeetingsFeatureEnabled = (state: RootState) =>
  state.data.meetings.settings.enabledCalendarIntegration &&
  state.data.features.enabledFeatures.includes(FeatureToggle.HybridMeetingsCalendarView) &&
  !state.data.meetings.isUserCalendarConsentInvalid &&
  state.data.meetings.userPreferences.enableCalendar;

export const mapMeetingUserPreferences = (preferences: Partial<MeetingsUserPreferences>) => {
  return {
    ...preferences,
    workingHours: mapWorkingHoursToWorkingHoursDto(preferences.workingHours),
  };
};

export const getCalendarDomainPreference = (state: RootState) => state.data.meetings.userPreferences.calendarDomain;
